import React from "react";
import { Phone, Mail, MapPin, LoaderCircle, CircleCheck, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const SHARED_PHONE = "+420 607 774 723";
const SHARED_EMAIL = "pradelna@struz.cz";
const SHARED_ADDRESS = "Vršovice, Prague, Czech Republic";
function ContactForm() {
  const {
    register,
    formState: {
      errors
    },
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit
  } = useWvcForm() as any;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center p-8 space-y-4 bg-green-50/50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
        <div className="size-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
          <CircleCheck className="size-6 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-green-800 dark:text-green-300">Odesláno!</h3>
        <p className="text-green-700 dark:text-green-400">{successMessage}</p>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <div className="space-y-2">
          <label htmlFor="name" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Jméno a příjmení <span className="text-destructive">*</span>
          </label>
          <input id="name" type="text" placeholder="Jan Novák" className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.name ? "border-destructive focus-visible:ring-destructive" : "border-input"}`} {...register("name", {
          required: "Jméno je povinné"
        })} />
          {errors.name && <p className="text-sm font-medium text-destructive">{errors.name.message}</p>}
        </div>

        {}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            E-mailová adresa <span className="text-destructive">*</span>
          </label>
          <input id="email" type="email" placeholder="jan.novak@example.com" className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.email ? "border-destructive focus-visible:ring-destructive" : "border-input"}`} {...register("email", {
          required: "E-mail je povinný",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Neplatný formát e-mailu"
          }
        })} />
          {errors.email && <p className="text-sm font-medium text-destructive">{errors.email.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Telefonní číslo
          </label>
          <input id="phone" type="tel" placeholder="+420 123 456 789" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" {...register("phone")} />
        </div>

        {}
        <div className="space-y-2">
          <label htmlFor="company" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            Společnost / Organizace
          </label>
          <input id="company" type="text" placeholder="Hotel XYZ" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" {...register("company")} />
        </div>
      </div>

      {}
      <div className="space-y-2">
        <label htmlFor="service_type" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Typ služby <span className="text-destructive">*</span>
        </label>
        <div className="relative">
          <select id="service_type" className={`flex h-10 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none ${errors.service_type ? "border-destructive focus:ring-destructive" : "border-input"}`} {...register("service_type", {
          required: "Vyberte prosím službu"
        })}>
            <option value="" disabled selected>Vyberte službu</option>
            <option value="Hotel">Hotel</option>
            <option value="Apartment">Apartmány</option>
            <option value="Restaurant">Restaurace</option>
            <option value="Spa">Wellness / Spa</option>
            <option value="Other">Jiné</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
            <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
            </svg>
          </div>
        </div>
        {errors.service_type && <p className="text-sm font-medium text-destructive">{errors.service_type.message}</p>}
      </div>

      {}
      <div className="space-y-2">
        <label htmlFor="message" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Vaše zpráva <span className="text-destructive">*</span>
        </label>
        <textarea id="message" placeholder="Popište nám své požadavky..." className={`flex min-h-[120px] w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.message ? "border-destructive focus-visible:ring-destructive" : "border-input"}`} {...register("message", {
        required: "Zpráva je povinná"
      })} />
        {errors.message && <p className="text-sm font-medium text-destructive">{errors.message.message}</p>}
      </div>

      {}
      {submitError && <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
          <CircleAlert className="size-4" />
          <p>{submitError}</p>
        </div>}

      {}
      <Button type="submit" className="w-full md:w-auto md:min-w-[200px]" disabled={isSubmitting}>
        {isSubmitting ? <>
            <LoaderCircle className="mr-2 size-4 animate-spin" />
            Odesílání...
          </> : "Odeslat poptávku"}
      </Button>
    </form>;
}
export default function JsmeVamKDispozici() {
  const contactCards = [{
    title: "Zavolejte nám",
    value: SHARED_PHONE,
    link: `tel:${SHARED_PHONE.replace(/\s/g, "")}`,
    icon: Phone,
    description: "Jsme k dispozici 24/7 pro urgentní řešení.",
    action: "Zavolat"
  }, {
    title: "Napište nám",
    value: SHARED_EMAIL,
    link: `mailto:${SHARED_EMAIL}`,
    icon: Mail,
    description: "Odpovídáme obvykle do 24 hodin.",
    action: "Napsat e-mail"
  }];
  return <section id="jsme-vám-k-dispozici" className="py-20 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Jsme vám k dispozici
          </h2>
          <p className="text-lg text-muted-foreground">
            Máte dotaz ohledně našich služeb nebo chcete nezávaznou nabídku? 
            Vyberte si způsob kontaktu, který vám nejvíce vyhovuje.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {contactCards.map((card, i) => <Link key={i} to={card.link} className="block group h-full" data-index={i}>
              <Card className="h-full transition-all duration-300 hover:shadow-lg hover:border-ring/50 border-border bg-card">
                
                <CardContent>
                  
                  <span className="text-sm font-semibold text-primary group-hover:text-ring transition-colors inline-flex items-center">
                    {card.action} &rarr;
                  </span>
                </CardContent>
              </Card>
            </Link>)}
        </div>

        {}
        <div className="max-w-3xl mx-auto">
          <Card className="border-border shadow-sm">
            <CardHeader className="text-center pb-8 border-b border-border/50">
              <CardTitle className="text-2xl">Kontaktní formulář</CardTitle>
              <CardDescription>
                Vyplňte formulář níže a my se vám ozveme s řešením na míru.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-8">
              <FormProvider formId="contact_form_contact" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              service_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              service_type: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Odeslat poptávku" successMessage="Děkujeme! Vaše zpráva byla úspěšně odeslána. Brzy vás budeme kontaktovat." errorMessage="Odeslání se nezdařilo. Zkuste to prosím znovu.">
                <ContactForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}